import { useState, useEffect } from 'react'
import { ExternalLink, CreditCard, User, RefreshCw } from 'lucide-react'
import Header from '../components/Header'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'
import toast from 'react-hot-toast'

interface Subscription {
  id: string
  status: string
  current_period_end: number
  cancel_at_period_end: boolean
}

const DashboardPage: React.FC = () => {
  const { user, refreshUser } = useAuth()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading] = useState(true)
  const [libreChatToken, setLibreChatToken] = useState<string | null>(null)
  const [libreChatUrl, setLibreChatUrl] = useState<string>('')
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return
      
      try {
        // Refresh user profile to get latest data
        await refreshUser()

        // Try to fetch subscription data (this might fail if no Stripe subscription)
        try {
          const subResponse = await api.get('/payment/subscription')
          setSubscription(subResponse.data.subscription)
        } catch (subError) {
          console.log('No subscription found, continuing...')
          // This is expected for test users without real Stripe subscriptions
        }

        // Fetch LibreChat token if user is active
        if (user.is_active) {
          console.log('Fetching LibreChat token for active user...')
          const tokenResponse = await api.get('/librechat/token')
          console.log('LibreChat token response:', tokenResponse.data)
          setLibreChatToken(tokenResponse.data.token)
          setLibreChatUrl(tokenResponse.data.librechat_url)
        } else {
          console.log('User is not active, skipping LibreChat token fetch')
        }
      } catch (error) {
        console.error('Failed to fetch data:', error)
        toast.error('Failed to load dashboard data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user])

  const handleManualRefresh = async () => {
    setRefreshing(true)
    try {
      await refreshUser()
      toast.success('User data refreshed!')
    } catch (error) {
      toast.error('Failed to refresh data')
    } finally {
      setRefreshing(false)
    }
  }

  const handleCancelSubscription = async () => {
    if (!subscription) return

    try {
      await api.post('/payment/cancel')
      toast.success('Subscription will be canceled at the end of the current period')
      // Refresh subscription data
      const response = await api.get('/payment/subscription')
      setSubscription(response.data.subscription)
    } catch (error) {
      toast.error('Failed to cancel subscription')
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-400 bg-green-900/30'
      case 'canceled':
        return 'text-red-400 bg-red-900/30'
      case 'past_due':
        return 'text-yellow-400 bg-yellow-900/30'
      default:
        return 'text-gray-400 bg-gray-800/30'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #0f172a 100%)'}}>
        <Header />
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2" style={{borderColor: '#3A9E5A'}}></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #0f172a 100%)'}}>
      <Header />
      
      <main className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold" style={{color: '#f9fafb'}}>Dashboard</h1>
            <p style={{color: '#d1d5db'}}>Manage your Inland Fiber AI subscription and access</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* User Info Card */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <User className="h-8 w-8 text-chateau-green mr-3" />
                  <h2 className="text-xl font-semibold" style={{color: '#f9fafb'}}>Account Info</h2>
                </div>
                <button
                  onClick={handleManualRefresh}
                  disabled={refreshing}
                  className="btn btn-secondary btn-sm flex items-center"
                  title="Refresh user data"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
              <div className="space-y-2">
                <p style={{color: '#d1d5db'}}>
                  <span className="font-medium">Name:</span> {user?.name}
                </p>
                <p style={{color: '#d1d5db'}}>
                  <span className="font-medium">Email:</span> {user?.email}
                </p>
                <p style={{color: '#d1d5db'}}>
                  <span className="font-medium">Status:</span>{' '}
                  <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(user?.subscription_status || 'inactive')}`}>
                    {user?.subscription_status || 'Inactive'}
                  </span>
                </p>
                {/* Debug info - remove this after fixing */}
                <details className="mt-4">
                  <summary className="text-xs text-gray-400 cursor-pointer">Debug Info (click to expand)</summary>
                  <pre className="text-xs text-gray-400 mt-2 bg-gray-800 p-2 rounded overflow-auto">
                    {JSON.stringify(user, null, 2)}
                  </pre>
                </details>
              </div>
            </div>

            {/* Subscription Card */}
            <div className="card p-6">
              <div className="flex items-center mb-4">
                <CreditCard className="h-8 w-8 text-chateau-green mr-3" />
                <h2 className="text-xl font-semibold" style={{color: '#f9fafb'}}>Subscription</h2>
              </div>
              {subscription ? (
                <div className="space-y-2">
                  <p style={{color: '#d1d5db'}}>
                    <span className="font-medium">Status:</span>{' '}
                    <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(subscription.status)}`}>
                      {subscription.status}
                    </span>
                  </p>
                  <p style={{color: '#d1d5db'}}>
                    <span className="font-medium">Next billing:</span>{' '}
                    {formatDate(subscription.current_period_end)}
                  </p>
                  {subscription.cancel_at_period_end && (
                    <p className="text-sm" style={{color: '#fbbf24'}}>
                      Subscription will be canceled at period end
                    </p>
                  )}
                  {subscription.status === 'active' && !subscription.cancel_at_period_end && (
                    <button
                      onClick={handleCancelSubscription}
                      className="btn btn-outline text-sm mt-2"
                    >
                      Cancel Subscription
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <p className="mb-4" style={{color: '#d1d5db'}}>No active subscription</p>
                  <a
                    href="/pricing"
                    className="btn btn-primary"
                  >
                    Subscribe Now
                  </a>
                </div>
              )}
            </div>

            {/* Inland Fiber AI Access Card */}
            <div className="card p-6">
              <div className="flex items-center mb-4">
                <ExternalLink className="h-8 w-8 text-chateau-green mr-3" />
                <h2 className="text-xl font-semibold" style={{color: '#f9fafb'}}>Inland Fiber AI Access</h2>
              </div>
              {user?.is_active ? (
                <div className="space-y-4">
                  <p style={{color: '#d1d5db'}}>
                    You have full access to Inland Fiber AI
                  </p>
                  <div className="space-y-2">
                    {libreChatToken && libreChatUrl ? (
                      <a
                        href={`${libreChatUrl}?token=${libreChatToken}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary w-full"
                      >
                        Open Inland Fiber AI
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    ) : (
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-chateau-green mx-auto mb-2"></div>
                        <p className="text-sm" style={{color: '#9ca3af'}}>Loading access...</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <p className="mb-4" style={{color: '#d1d5db'}}>
                    {user?.subscription_status === 'inactive' 
                      ? 'Subscribe to access Inland Fiber AI'
                      : 'Loading access...'
                    }
                  </p>
                  {user?.subscription_status === 'inactive' && (
                    <a
                      href="/pricing"
                      className="btn btn-primary"
                    >
                      Get Access
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Usage Stats */}
          <div className="mt-8">
            <div className="card p-6">
              <h2 className="text-xl font-semibold mb-4" style={{color: '#f9fafb'}}>Usage Statistics</h2>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{color: '#3A9E5A'}}>0</div>
                  <div style={{color: '#d1d5db'}}>Conversations</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{color: '#3A9E5A'}}>0</div>
                  <div style={{color: '#d1d5db'}}>Messages</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{color: '#3A9E5A'}}>0%</div>
                  <div style={{color: '#d1d5db'}}>Usage</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default DashboardPage
